/**
 * Custom Fields manager — typed field-schema for leads & contacts (scope 'crm'),
 * and the workspace FLOW-VARIABLE REGISTRY (scope 'flow').
 *
 * - 'crm' (default): lead/contact custom fields whose VALUES live in the record's
 *   customAttributes. Vertical packs seed some (source='vertical'); tenants add
 *   their own. Full CRUD via /admin/field-definitions — gated by the VERTICALS
 *   module (Settings › Custom Fields tab).
 * - 'flow': reusable {{variable}} NAMES an automation reads/writes. These hold no
 *   stored value — each workflow run sets its own and it's cleared when the flow
 *   ends. Create/delete only (a name anchors {{references}}, so no rename), and
 *   advisory (a flow may still use an unregistered name). Mutations go through
 *   /automation/flow-variables — gated by the AUTOMATION module, so a tenant
 *   manages flow variables WITHOUT needing the Verticals module. Rendered on its
 *   own Settings › Flow Variables tab.
 *
 * Reads for both scopes use the open GET /admin/field-definitions (no module
 * gate), so the two stores share one fetch and the builder's pickers can list
 * flow variables with no dependency on either module.
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  Menu,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { Add, DeleteOutline, EditOutlined, MoreVert } from '@mui/icons-material';
import adminApi, { FieldDefinition } from '@/services/admin.api';

const TYPES: FieldDefinition['type'][] = ['string', 'number', 'date', 'boolean', 'enum'];

type MapsTo = NonNullable<FieldDefinition['mapsTo']>;
const MAPS_TO_LABEL: Record<MapsTo, string> = {
  interest: 'Interest',
  budget: 'Budget',
  estimatedValue: 'Estimated value',
};
/** Targets a field of this type may stand in for (mirrors the server's checkMapsTo). */
const mapsToOptions = (type: FieldDefinition['type'] | undefined): MapsTo[] =>
  type === 'number' ? ['estimatedValue', 'budget'] : type === 'string' || type === 'enum' ? ['interest'] : [];

type Entity = 'lead' | 'contact' | 'flow';
type Scope = 'crm' | 'flow';

/** Normalise a typed variable/field key — no spaces, reference-safe. */
const sanitizeKey = (s: string) => s.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');

/** A usable {{token}} — same rule as the API mapping editor (isValidVariableName),
 *  so a registered flow variable can never be a name a mapping would reject. */
const isValidFlowName = (s: string) => /^[A-Za-z_][A-Za-z0-9_]{0,59}$/.test(s);

interface Props {
  /** 'crm' = lead/contact fields (verticals); 'flow' = flow-variable registry (automation). */
  scope?: Scope;
}

const CustomFieldsManager: React.FC<Props> = ({ scope = 'crm' }) => {
  const isFlow = scope === 'flow';
  // In 'crm' scope the tab switches entity; in 'flow' scope it's fixed.
  const [entity, setEntity] = useState<Entity>(isFlow ? 'flow' : 'lead');
  const [defs, setDefs] = useState<FieldDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // null = adding; an id = editing that field (key/entity locked while editing).
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<FieldDefinition>>({ type: 'string', key: '', label: '' });
  // Per-entity cap on field definitions (0 = unlimited), from the plan tier.
  const [limit, setLimit] = useState(0);
  // A field/variable awaiting delete confirmation (deleting is immediate and irreversible).
  const [confirmDelete, setConfirmDelete] = useState<FieldDefinition | null>(null);

  const load = () => {
    setLoading(true);
    adminApi
      .getFieldDefinitions()
      .then(setDefs)
      .catch((e: any) => setError(e?.message ?? 'Failed to load'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  useEffect(() => {
    adminApi
      .getVerticalContext()
      .then((c) => setLimit(c.fieldDefinitionsLimit ?? 0))
      .catch(() => {});
  }, []);

  const rows = defs.filter((d) => d.entity === entity).sort((a, b) => a.order - b.order);
  const atLimit = limit > 0 && rows.length >= limit;

  const openAdd = () => {
    if (atLimit) return;
    setEditingId(null);
    setForm({ type: 'string', key: '', label: '', description: '', order: rows.length, required: false });
    setOpen(true);
  };

  const openEdit = (d: FieldDefinition) => {
    setEditingId(d._id);
    setForm({ ...d });
    setOpen(true);
  };

  // Flow variables carry no label distinct from their name, so the key doubles as
  // the label — one "Variable name" field instead of two, and it must be a valid
  // {{token}}.
  const canSave = isFlow ? isValidFlowName((form.key ?? '').trim()) : !!form.key && !!form.label;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      if (isFlow) {
        // Flow variables are create/delete only (no edit) — the dialog only ever
        // adds, and mutations go through the automation-gated endpoint.
        const key = (form.key ?? '').trim();
        if (!key) return;
        await adminApi.createFlowVariable({
          key,
          type: form.type ?? 'string',
          ...(form.description ? { description: form.description } : {}),
        });
      } else if (editingId) {
        // key/entity are immutable server-side (they anchor value storage) — send
        // only the editable fields that are actually set.
        const payload: Partial<FieldDefinition> = {};
        if (form.label !== undefined) payload.label = form.label;
        if (form.type !== undefined) payload.type = form.type;
        if (form.description !== undefined) payload.description = form.description;
        if (form.options !== undefined) payload.options = form.options;
        if (form.required !== undefined) payload.required = form.required;
        if (form.order !== undefined) payload.order = form.order;
        // Only send a mapping that actually changed — any mapsTo in the body triggers a re-score.
        if (entity === 'lead') {
          const before = defs.find((d) => d._id === editingId)?.mapsTo ?? null;
          const after = form.mapsTo ?? null;
          if (before !== after) payload.mapsTo = after;
        }
        await adminApi.updateFieldDefinition(editingId, payload);
      } else {
        const { mapsTo, ...rest } = form;
        await adminApi.createFieldDefinition({
          ...rest,
          entity,
          ...(entity === 'lead' && mapsTo ? { mapsTo } : {}),
        });
      }
      setOpen(false);
      load();
    } catch (e: any) {
      setError(e?.response?.data?.error?.message ?? e?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const del = async (id: string) => {
    try {
      if (isFlow) await adminApi.deleteFlowVariable(id);
      else await adminApi.deleteFieldDefinition(id);
      load();
    } catch (e: any) {
      setError(e?.response?.data?.error?.message ?? e?.message ?? 'Delete failed');
    }
  };

  const heading = isFlow ? 'Variables' : 'Custom Fields';
  const blurb = isFlow
    ? 'Reusable names for data your automations pass around. Each workflow sets its own value; cleared when the flow ends.'
    : 'Typed fields for your leads & contacts. Vertical fields are seeded automatically.';
  const countLabel = isFlow ? 'variables' : `${entity} fields`;

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {heading}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560 }}>
              {blurb}
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Typography variant="body2" color={atLimit ? 'error' : 'text.secondary'}>
              {limit > 0 ? `${rows.length} / ${limit} ${countLabel}` : `${rows.length} ${countLabel}`}
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={openAdd} disabled={atLimit} sx={{ borderRadius: 2 }}>
              {isFlow ? 'Add variable' : 'Add field'}
            </Button>
          </Stack>
        </Stack>

        {atLimit && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            You've reached your plan's limit of {limit} {countLabel}. Upgrade to add more.
          </Alert>
        )}

        {!isFlow && (
          <Tabs
            value={entity}
            onChange={(_, v) => setEntity(v)}
            sx={{ mb: 2, minHeight: 0, '& .MuiTab-root': { minHeight: 42, textTransform: 'none', fontWeight: 600 } }}
          >
            <Tab value="lead" label="Lead" />
            <Tab value="contact" label="Contact" />
          </Tabs>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : isFlow ? (
          <FlowTable rows={rows} onDelete={setConfirmDelete} />
        ) : (
          <FieldTable rows={rows} onEdit={openEdit} onDelete={setConfirmDelete} />
        )}
      </CardContent>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingId ? 'Edit' : 'Add'} {isFlow ? 'variable' : `${entity} field`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {isFlow ? (
              <TextField
                label="Variable name"
                size="small"
                value={form.key || ''}
                onChange={(e) => setForm((f) => ({ ...f, key: sanitizeKey(e.target.value) }))}
                placeholder="e.g. order_number"
                error={!!form.key && !isValidFlowName(form.key)}
                helperText={
                  form.key && !isValidFlowName(form.key)
                    ? 'Must start with a letter or _ , then letters, numbers and _ (max 60).'
                    : 'Reference it in flows as {{name}}. Letters, numbers and _ , no spaces.'
                }
                fullWidth
                InputProps={{ sx: { fontFamily: 'monospace' } }}
              />
            ) : (
              <>
                <TextField
                  label="Label"
                  size="small"
                  value={form.label || ''}
                  onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Key"
                  size="small"
                  value={form.key || ''}
                  onChange={(e) => setForm((f) => ({ ...f, key: sanitizeKey(e.target.value) }))}
                  disabled={!!editingId}
                  helperText={
                    editingId
                      ? "Key can't be changed after creation (it anchors stored values)."
                      : 'Stored on the record; no spaces.'
                  }
                  fullWidth
                />
              </>
            )}
            <TextField
              select
              label="Type"
              size="small"
              value={form.type || 'string'}
              onChange={(e) =>
                setForm((f) => {
                  const type = e.target.value as FieldDefinition['type'];
                  // Drop a mapping the new type can't carry (e.g. text -> number).
                  const keep = f.mapsTo && mapsToOptions(type).includes(f.mapsTo);
                  return { ...f, type, mapsTo: keep ? (f.mapsTo ?? null) : null };
                })
              }
              fullWidth
              helperText={isFlow ? 'Optional — a hint only; flow values are rendered as text today.' : undefined}
            >
              {TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
            {!isFlow && form.type === 'enum' && (
              <TextField
                label="Options (comma-separated)"
                size="small"
                value={(form.options || []).join(', ')}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  }))
                }
                fullWidth
              />
            )}
            {!isFlow && entity === 'lead' && mapsToOptions(form.type).length > 0 && (
              <TextField
                select
                label="Counts as (for lead scoring)"
                size="small"
                value={form.mapsTo ?? ''}
                onChange={(e) =>
                  setForm((f) => ({ ...f, mapsTo: (e.target.value || null) as MapsTo | null }))
                }
                fullWidth
                helperText="Scoring rules written for the core field (e.g. Interest) will also read this one."
              >
                <MenuItem value="">Nothing — just a custom field</MenuItem>
                {mapsToOptions(form.type).map((m) => (
                  <MenuItem key={m} value={m}>
                    {MAPS_TO_LABEL[m]}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label="Description (optional)"
              size="small"
              value={form.description || ''}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder={isFlow ? 'What this variable holds' : 'What this field holds'}
              fullWidth
              multiline
              rows={2}
            />
            {!isFlow && (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.required}
                    onChange={(e) => setForm((f) => ({ ...f, required: e.target.checked }))}
                  />
                }
                label="Required"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving || !canSave} sx={{ borderRadius: 2 }}>
            {saving ? 'Saving…' : editingId ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete {isFlow ? 'variable' : 'field'}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {isFlow ? (
              <>
                <b>{confirmDelete ? `{{${confirmDelete.key}}}` : ''}</b> will no longer appear in the
                variable pickers. Workflows that already use it keep working.
              </>
            ) : (
              <>
                <b>{confirmDelete?.label}</b> will be removed from forms and filters
                {confirmDelete?.mapsTo ? ' and will stop counting toward lead scoring' : ''}. Values already
                saved on records are kept.
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            sx={{ borderRadius: 2 }}
            onClick={() => {
              const id = confirmDelete?._id;
              setConfirmDelete(null);
              if (id) del(id);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

const fmtDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

/** The ⋮ actions menu used by both tables — one affordance for every row. */
const RowActions: React.FC<{
  label: string;
  items: Array<{ label: string; icon: React.ReactNode; onClick: () => void; danger?: boolean }>;
}> = ({ label, items }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <Tooltip title="Actions">
        <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)} aria-label={label}>
          <MoreVert fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { minWidth: 160, borderRadius: 2 } }}
      >
        {items.map((it) => (
          <MenuItem
            key={it.label}
            dense
            onClick={() => {
              setAnchor(null);
              it.onClick();
            }}
            sx={{ color: it.danger ? 'error.main' : 'text.primary' }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}>{it.icon}</ListItemIcon>
            <ListItemText primary={it.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

/** lead / contact table — full schema with edit. */
const FieldTable: React.FC<{
  rows: FieldDefinition[];
  onEdit: (d: FieldDefinition) => void;
  onDelete: (d: FieldDefinition) => void;
}> = ({ rows, onEdit, onDelete }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>Label</TableCell>
        <TableCell>Key</TableCell>
        <TableCell>Type</TableCell>
        <TableCell>Source</TableCell>
        <TableCell>Created</TableCell>
        <TableCell align="right" sx={{ width: 56 }} />
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.length === 0 ? (
        <TableRow>
          <TableCell colSpan={6}>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
              No custom fields yet.
            </Typography>
          </TableCell>
        </TableRow>
      ) : (
        rows.map((d) => (
          <TableRow key={d._id} hover>
            <TableCell>
              {d.label}
              {d.required && <Chip size="small" label="required" sx={{ ml: 1 }} />}
            </TableCell>
            <TableCell>
              <code>{d.key}</code>
            </TableCell>
            <TableCell>
              {d.type}
              {d.type === 'enum' && d.options?.length ? ` (${d.options.length})` : ''}
              {d.mapsTo && (
                <Chip size="small" variant="outlined" label={`= ${MAPS_TO_LABEL[d.mapsTo]}`} sx={{ ml: 1 }} />
              )}
            </TableCell>
            <TableCell>
              <Chip
                size="small"
                variant="outlined"
                color={d.source === 'vertical' ? 'primary' : 'default'}
                label={d.source}
              />
            </TableCell>
            <TableCell>
              <Tooltip title={d.createdAt ? new Date(d.createdAt).toLocaleString() : ''}>
                <Typography variant="body2" color="text.secondary" component="span">
                  {fmtDate(d.createdAt)}
                </Typography>
              </Tooltip>
            </TableCell>
            <TableCell align="right">
              <RowActions
                label={`Actions for ${d.label}`}
                items={[
                  { label: 'Edit', icon: <EditOutlined fontSize="small" />, onClick: () => onEdit(d) },
                  { label: 'Delete', icon: <DeleteOutline fontSize="small" />, onClick: () => onDelete(d), danger: true },
                ]}
              />
            </TableCell>
          </TableRow>
        ))
      )}
    </TableBody>
  </Table>
);

/** Variable table — create/delete only (no edit: a name anchors references). */
const FlowTable: React.FC<{ rows: FieldDefinition[]; onDelete: (d: FieldDefinition) => void }> = ({ rows, onDelete }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>Variable</TableCell>
        <TableCell>Type</TableCell>
        <TableCell>Description</TableCell>
        <TableCell>Created</TableCell>
        <TableCell align="right" sx={{ width: 56 }} />
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.length === 0 ? (
        <TableRow>
          <TableCell colSpan={5}>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
              No variables yet. Add names you reuse across automations, then pick them on the builder.
            </Typography>
          </TableCell>
        </TableRow>
      ) : (
        rows.map((d) => (
          <TableRow key={d._id} hover>
            <TableCell>
              <code>{`{{${d.key}}}`}</code>
            </TableCell>
            <TableCell>{d.type}</TableCell>
            <TableCell>
              <Typography variant="body2" color="text.secondary">
                {d.description || '—'}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2" color="text.secondary">
                {d.createdAt ? new Date(d.createdAt).toLocaleDateString() : '—'}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <RowActions
                label={`Actions for ${d.key}`}
                items={[
                  { label: 'Delete', icon: <DeleteOutline fontSize="small" />, onClick: () => onDelete(d), danger: true },
                ]}
              />
            </TableCell>
          </TableRow>
        ))
      )}
    </TableBody>
  </Table>
);

export default CustomFieldsManager;
